import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { sizingClass, TEXT_STYLE, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';
import type { ComponentFn } from '@littlebird/action-cards-view/react';

type StepState = 'queued' | 'running' | 'done' | 'failed';

type StepItem = {
  value: string;
  label: string;
  selected: boolean;
  detail?: string;
  state?: StepState;
  live?: string;
};

// The current step is the running one; with none running, the first step not yet
// done; with every step done, the last one
function currentStepIndex(items: StepItem[]): number {
  const running = items.findIndex((item) => item.state === 'running');
  if (running !== -1) return running;
  const notDone = items.findIndex((item) => item.state !== 'done');
  if (notDone !== -1) return notDone;
  return items.length - 1;
}

function StepDot({ state }: { state: StepState | undefined }) {
  if (state === 'done') {
    return (
      <span
        title="done"
        className="flex size-[calc(8*var(--ac-u))] shrink-0 items-center justify-center rounded-full bg-kind-done"
      >
        <Icons.check
          className="size-[calc(6*var(--ac-u))] text-white"
          aria-hidden="true"
        />
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span
        title="failed"
        className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full bg-danger-9"
      />
    );
  }
  if (state === 'running') {
    return (
      <span
        title="running"
        className="size-[calc(8*var(--ac-u))] shrink-0 animate-lb-pulse rounded-full bg-foreground motion-reduce:animate-none"
      />
    );
  }
  return (
    <span
      title="queued"
      className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full border border-border-strong"
    />
  );
}

function StepRow({ item }: { item: StepItem }) {
  const secondary =
    item.state === 'running' ? (item.live ?? item.detail) : item.detail;

  return (
    <XStack className="min-w-0 items-start gap-[var(--ac-gap-sm)]">
      <span className="flex h-[var(--ac-note-lh)] items-center">
        <StepDot state={item.state} />
      </span>
      <YStack className="min-w-0 gap-[calc(2*var(--ac-u))]">
        <span className={cn(TEXT_STYLE.note, 'font-semibold')}>
          {item.label}
        </span>
        {secondary ? (
          <span className={cn(TEXT_STYLE.note, TONE_CLASS.muted)}>
            {secondary}
          </span>
        ) : null}
      </YStack>
    </XStack>
  );
}

// The work behind a card, one row per step. `collapsed` shows only the step in
// progress, with a text link to reveal the rest
export const Steps: ComponentFn<typeof catalog, 'Steps'> = ({ props }) => {
  const items = props.items;
  const [expanded, setExpanded] = useState(false);
  const showAll = props.collapsed !== true || expanded;
  const rows = showAll
    ? items
    : items.length > 0
      ? [items[currentStepIndex(items)]]
      : [];

  return (
    <YStack
      className={cn(
        'min-w-0 gap-[var(--ac-gap-sm)]',
        sizingClass(props.sizing)
      )}
    >
      {props.label ? (
        <span className={cn(TEXT_STYLE.meta, TONE_CLASS.muted)}>
          {props.label}
        </span>
      ) : null}
      <YStack className="min-w-0 gap-[var(--ac-gap-sm)]">
        {rows.map((item) => (
          <StepRow key={item.value} item={item} />
        ))}
      </YStack>
      {!showAll && items.length > 0 ? (
        <button
          type="button"
          className={cn(
            'self-start text-foreground-muted underline-offset-2 hover:text-foreground hover:underline',
            TEXT_STYLE.meta
          )}
          onClick={() => setExpanded(true)}
        >
          Show all {items.length} steps
        </button>
      ) : null}
    </YStack>
  );
};
